// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Fiscal/NotasFiscais.tsx
//  Listagem paginada de NF-e com transmissão e cancelamento
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FileText, Send, XCircle, Search, ChevronLeft, ChevronRight, RefreshCw, Plus, Eye, Download, Printer, Ban, FileArchive, FilterX } from 'lucide-react';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';
import { toast } from '@/components/ui/sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import { useNotaFiscal } from '@/features/fiscal/hooks/useNotaFiscal';
import { notaFiscalService } from '@/features/fiscal/services/notaFiscalService';
import { ConfirmarTransmissaoDialog } from '@/features/fiscal/components/ConfirmarTransmissaoDialog';
import { InutilizarFaixaDialog } from '@/features/fiscal/components/InutilizarFaixaDialog';
import { ImportarNotasEmitidasDialog } from '@/features/fiscal/components/ImportarNotasEmitidasDialog';
import { usePermissoes } from '@/contexts/PermissoesContext';
import { FORMULARIOS } from '@/domain/formularios';
import { statusDeCStat } from '@/features/fiscal/types';
import type { StatusNFe, NotaFiscalResumoDto } from '@/features/fiscal/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

const PAGE_SIZES = [10, 20, 50] as const;

const statusConfig: Record<StatusNFe, { label: string; className: string }> = {
  emitida:   { label: 'Autorizada', className: 'bg-green-500/10 text-green-600'   },
  cancelada: { label: 'Cancelada',  className: 'bg-red-500/10 text-red-600'       },
  denegada:  { label: 'Denegada',   className: 'bg-orange-500/10 text-orange-600' },
  pendente:  { label: 'Pendente',   className: 'bg-yellow-500/10 text-yellow-600' },
  inutilizada: { label: 'Inutilizada', className: 'bg-slate-500/10 text-slate-600' },
};

// A data de emissão é só o dia (sem hora): formata sem converter fuso, senão vira a véspera.
const dataDeEmissao = (nota: { dtEmissao?: string | null; dtCriacao: string }) =>
  nota.dtEmissao
    ? nota.dtEmissao.slice(0, 10).split('-').reverse().join('/')
    : new Date(nota.dtCriacao).toLocaleDateString('pt-BR');

const chaveResumida = (chave: string | null) =>
  chave ? `...${chave.slice(-12)}` : '—';

const moeda = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const baixarXml = (nota: NotaFiscalResumoDto) =>
  notaFiscalService.baixarXml(nota.id, nota.chaveNota).catch((err) =>
    toast.error('Não foi possível baixar o XML.', { description: getApiErrorMessage(err) }));

const abrirDanfe = (nota: NotaFiscalResumoDto) =>
  notaFiscalService.abrirDanfe(nota.id).catch((err) =>
    toast.error('Não foi possível gerar o DANFE.', { description: getApiErrorMessage(err) }));

const NotasFiscais = () => {
  const navigate = useNavigate();
  const [pagina,        setPagina]        = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);
  const [busca,         setBusca]         = useState('');
  const [statusFiltro,  setStatusFiltro]  = useState<string>('todos');
  const [serie,         setSerie]         = useState('');
  // NF-e por padrão (como sempre foi esta tela); NFC-e ou as duas pelo filtro.
  const [modelo,        setModelo]        = useState<'55' | '65' | '0'>('55');
  // Abre nas notas de hoje, mais recentes primeiro: com o tempo a lista inteira fica grande demais.
  const periodo   = usePeriodo('hoje', () => setPagina(1));
  const ordenacao = useOrdenacao<'emissao' | 'numero'>('emissao', true, () => setPagina(1));

  // Cancelamento dialog
  const [cancelandoNota,   setCancelandoNota]   = useState<NotaFiscalResumoDto | null>(null);
  const [justificativa,    setJustificativa]    = useState('');
  const [transmitindoNota, setTransmitindoNota] = useState<NotaFiscalResumoDto | null>(null);
  const [inutilizandoFaixa, setInutilizandoFaixa] = useState(false);
  const [importando, setImportando] = useState(false);
  const queryClient = useQueryClient();
  const { temPermissao } = usePermissoes();
  const podeImportar = temPermissao(FORMULARIOS.IMPORTACAO_DADOS, 'incluir');

  // A busca vai ao servidor (com o histórico importado são milhares de notas): espera a digitação parar.
  const [buscaEnviada, setBuscaEnviada] = useState('');
  const [serieEnviada, setSerieEnviada] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setBuscaEnviada(busca.trim()); setSerieEnviada(serie.trim()); }, 400);
    return () => clearTimeout(t);
  }, [busca, serie]);

  const {
    notas, total, totalPaginas, contadores,
    isLoading, isFetching,
    transmitir, cancelar, isTransmitindo, isCancelando,
  } = useNotaFiscal(pagina, tamanhoPagina, {
    busca: buscaEnviada,
    situacao: statusFiltro,
    serie: serieEnviada,
    modelo: Number(modelo) as 55 | 65 | 0,
    ...periodo.intervalo,
    ordenarPor: ordenacao.ordenarPor,
    decrescente: ordenacao.decrescente,
  });

  // Busca e situação já vêm filtradas do servidor.
  const notasFiltradas = notas;

  // KPIs do filtro inteiro (servidor); sem os contadores, cai na contagem da página.
  const daPagina = (s: StatusNFe) => notas.filter((n) => statusDeCStat(n.cStat) === s).length;
  const totalEmitidas   = contadores?.emitidas   ?? daPagina('emitida');
  const totalPendentes  = contadores?.pendentes  ?? daPagina('pendente');
  const totalCanceladas = contadores?.canceladas ?? daPagina('cancelada');
  const totalDenegadas  = contadores?.denegadas  ?? daPagina('denegada');
  const rotuloKpi = contadores ? 'No filtro' : 'Nesta página';

  const limparFiltros = () => {
    setBusca(''); setSerie(''); setStatusFiltro('todos'); setModelo('55');
    periodo.setPeriodo('hoje');
    setPagina(1);
  };

  const firstItem = (pagina - 1) * tamanhoPagina + 1;
  const lastItem  = Math.min(pagina * tamanhoPagina, total);

  const handlePageSizeChange = (v: string) => { setTamanhoPagina(Number(v)); setPagina(1); };

  const handleCancelar = () => {
    if (!cancelandoNota || justificativa.trim().length < 15) return;
    cancelar({ id: cancelandoNota.id, req: { justificativa } });
    setCancelandoNota(null);
    setJustificativa('');
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Notas Fiscais</h1>
          <p className="text-muted-foreground mt-1">Gerencie e transmita suas notas fiscais eletrônicas</p>
        </div>
        <div className="flex gap-2">
          {podeImportar && (
            <Button variant="outline" className="gap-2" onClick={() => setImportando(true)}
              title="Traz as NF-e já emitidas em outro sistema, pelos XML autorizados">
              <FileArchive className="h-4 w-4" />
              Importar notas emitidas
            </Button>
          )}
          <Button variant="outline" className="gap-2" onClick={() => setInutilizandoFaixa(true)}>
            <Ban className="h-4 w-4" />
            Inutilizar números
          </Button>
          <Button className="gap-2" onClick={() => navigate('/fiscal/notas-fiscais/nova')}>
            <Plus className="h-4 w-4" />
            Nova NF-e
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Autorizadas', value: totalEmitidas,   color: 'text-green-500'  },
          { label: 'Pendentes',   value: totalPendentes,  color: 'text-yellow-500' },
          { label: 'Canceladas',  value: totalCanceladas, color: 'text-red-500'    },
          { label: 'Denegadas',   value: totalDenegadas,  color: 'text-orange-500' },
        ].map(({ label, value, color }) => (
          <Card key={label}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${color}`}>{value}</div>
              <p className="text-xs text-muted-foreground mt-1">{rotuloKpi}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
            <CardTitle className="whitespace-nowrap">Lista de Notas Fiscais</CardTitle>
            <div className="flex flex-col sm:flex-row sm:flex-wrap md:justify-end gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Número, chave, CNPJ/CPF ou cliente..."
                  value={busca}
                  onChange={(e) => { setBusca(e.target.value); setPagina(1); }}
                  className="pl-9"
                />
              </div>
              <PeriodoFiltro estado={periodo} rotuloData="Emissão" />
              <Select value={statusFiltro} onValueChange={(v) => { setStatusFiltro(v); setPagina(1); }}>
                <SelectTrigger className="w-full sm:w-44">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os status</SelectItem>
                  <SelectItem value="emitida">Autorizada</SelectItem>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="cancelada">Cancelada</SelectItem>
                  <SelectItem value="denegada">Denegada</SelectItem>
                  <SelectItem value="inutilizada">Inutilizada</SelectItem>
                </SelectContent>
              </Select>
              <Input
                aria-label="Série"
                placeholder="Série"
                value={serie}
                maxLength={3}
                onChange={(e) => { setSerie(e.target.value); setPagina(1); }}
                className="w-full sm:w-20"
              />
              <Select value={modelo} onValueChange={(v) => { setModelo(v as '55' | '65' | '0'); setPagina(1); }}>
                <SelectTrigger className="w-full sm:w-36" aria-label="Modelo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="55">NF-e (55)</SelectItem>
                  <SelectItem value="65">NFC-e (65)</SelectItem>
                  <SelectItem value="0">NF-e e NFC-e</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="ghost" size="icon" onClick={limparFiltros} title="Limpar filtros">
                <FilterX className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <CabecalhoOrdenavel coluna="numero" ordenacao={ordenacao}>Número</CabecalhoOrdenavel>
                  <TableHead>Série</TableHead>
                  <TableHead>Modelo</TableHead>
                  <TableHead>Destinatário</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Chave de Acesso</TableHead>
                  <CabecalhoOrdenavel coluna="emissao" ordenacao={ordenacao}>Emissão</CabecalhoOrdenavel>
                  <TableHead>Status SEFAZ</TableHead>
                  <TableHead>Ambiente</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">Carregando...</TableCell>
                  </TableRow>
                ) : notasFiltradas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                      {periodo.periodo === 'hoje' ? 'Nenhuma nota fiscal emitida hoje — mude o período para ver as anteriores'
                        : 'Nenhuma nota fiscal encontrada'}
                    </TableCell>
                  </TableRow>
                ) : (
                  notasFiltradas.map((nota) => {
                    const status = statusDeCStat(nota.cStat);
                    const cfg    = statusConfig[status];
                    return (
                      <TableRow key={nota.id}>
                        <TableCell className="font-medium">
                          {nota.numeroNota > 0 ? String(nota.numeroNota).padStart(6, '0') : '—'}
                        </TableCell>
                        <TableCell>{nota.serie ?? '—'}</TableCell>
                        <TableCell>{nota.modelo === 65 ? 'NFC-e' : nota.modelo === 55 ? 'NF-e' : '—'}</TableCell>
                        <TableCell className="max-w-[220px]">
                          <p className="truncate" title={nota.nomeRazao ?? undefined}>{nota.nomeRazao ?? '—'}</p>
                          {nota.cnpjCpfDestinatario && (
                            <p className="text-xs text-muted-foreground">{nota.cnpjCpfDestinatario}</p>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{moeda(nota.totalNota)}</TableCell>
                        <TableCell className="text-xs font-mono text-muted-foreground" title={nota.chaveNota ?? undefined}>
                          {chaveResumida(nota.chaveNota)}
                        </TableCell>
                        <TableCell>{dataDeEmissao(nota)}</TableCell>
                        <TableCell>
                          <div>
                            <Badge className={cfg.className}>
                              {nota.situacao === 'rejeitada' ? 'Rejeitada' : nota.situacao === 'rascunho' ? 'Rascunho'
                                : nota.situacao === 'inutilizada' ? 'Inutilizada' : cfg.label}
                            </Badge>
                            {nota.isImportada && (
                              <Badge variant="outline" className="ml-1" title="Emitida em outro sistema e importada pelo XML">
                                Importada
                              </Badge>
                            )}
                            {nota.cStat && (
                              <p className="text-xs text-muted-foreground mt-0.5">{nota.cStat} — {nota.xMotivo}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={nota.isAmbienteProducao ? '' : 'text-yellow-600 border-yellow-500'}>
                            {nota.isAmbienteProducao ? 'Produção' : 'Homologação'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7"
                              onClick={() => navigate(`/fiscal/notas-fiscais/${nota.id}`)}
                              title="Ver detalhes">
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                            {status === 'pendente' && (
                              <Button size="sm" variant="outline" className="gap-1"
                                onClick={() => setTransmitindoNota(nota)}
                                disabled={isTransmitindo}
                                title="Transmitir à SEFAZ">
                                <Send className="h-3 w-3" />Transmitir
                              </Button>
                            )}
                            {nota.situacao === 'autorizada' && (
                              <>
                                <Button size="icon" variant="ghost" className="h-7 w-7"
                                  onClick={() => abrirDanfe(nota)} title="Imprimir DANFE">
                                  <Printer className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7"
                                  onClick={() => baixarXml(nota)} title="Baixar XML autorizado">
                                  <Download className="h-3.5 w-3.5" />
                                </Button>
                              </>
                            )}
                            {status === 'emitida' && !nota.isImportada && (
                              <Button size="sm" variant="ghost"
                                className="text-destructive hover:text-destructive"
                                onClick={() => { setCancelandoNota(nota); setJustificativa(''); }}
                                title="Cancelar NF-e">
                                <XCircle className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Paginação */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              {total === 0
                ? 'Nenhum registro'
                : `Mostrando ${firstItem}–${lastItem} de ${total} registro${total !== 1 ? 's' : ''}`}
              {isFetching && !isLoading && <span className="ml-2 text-xs">(atualizando...)</span>}
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">Itens por página:</span>
                <Select value={String(tamanhoPagina)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-[80px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(1)} disabled={pagina === 1}>«</Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p - 1)} disabled={pagina === 1}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                  let page: number;
                  if      (totalPaginas <= 5)          page = i + 1;
                  else if (pagina <= 3)                 page = i + 1;
                  else if (pagina >= totalPaginas - 2)  page = totalPaginas - 4 + i;
                  else                                  page = pagina - 2 + i;
                  return (
                    <Button key={page} variant={pagina === page ? 'default' : 'outline'}
                      size="icon" className="h-8 w-8 text-xs" onClick={() => setPagina(page)}>{page}</Button>
                  );
                })}
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p + 1)} disabled={pagina >= totalPaginas}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(totalPaginas)} disabled={pagina >= totalPaginas}>»</Button>
              </div>
              <span className="text-sm text-muted-foreground whitespace-nowrap">Página {pagina} de {totalPaginas}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <InutilizarFaixaDialog
        open={inutilizandoFaixa}
        onFechar={() => setInutilizandoFaixa(false)}
        onInutilizado={() => queryClient.invalidateQueries({ queryKey: ['notas-fiscais'] })}
      />

      <ImportarNotasEmitidasDialog
        open={importando}
        onFechar={() => setImportando(false)}
        onImportado={() => queryClient.invalidateQueries({ queryKey: ['notas-fiscais'] })}
      />

      <ConfirmarTransmissaoDialog
        open={!!transmitindoNota}
        producao={!!transmitindoNota?.isAmbienteProducao}
        onConfirmar={() => { if (transmitindoNota) transmitir(transmitindoNota.id); setTransmitindoNota(null); }}
        onFechar={() => setTransmitindoNota(null)}
      />

      {/* Dialog: cancelar NF-e */}
      <Dialog open={!!cancelandoNota} onOpenChange={(o) => { if (!o) setCancelandoNota(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar NF-e {cancelandoNota ? String(cancelandoNota.numeroNota).padStart(6, '0') : ''}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Informe a justificativa do cancelamento (mínimo 15 caracteres). Esta ação será enviada à SEFAZ.
              {' '}Cancelando por aqui, <strong>o pedido não é estornado</strong> (estoque e contas a receber ficam como estão).
              Para cancelar a nota e estornar o pedido de uma vez, abra a nota e cancele pelo detalhe.
            </p>
            <div className="space-y-1">
              <Label>Justificativa *</Label>
              <Textarea rows={4}
                placeholder="Descreva o motivo do cancelamento..."
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
              />
              <p className="text-xs text-muted-foreground text-right">{justificativa.length} / 255 caracteres</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelandoNota(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleCancelar}
              disabled={isCancelando || justificativa.trim().length < 15}>
              {isCancelando ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Cancelando...</>
              ) : (
                <><XCircle className="h-4 w-4 mr-2" />Confirmar Cancelamento</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotasFiscais;
